import { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BREAKPOINTS_WIDTHS, formatMoney, useWindowSize } from '@belong/common';
import { IconCheck } from '@belong/icons';
import { Button, Text, Image } from '@belong/ui';
import cx from 'clsx';
import Selector from 'components/Selector/Selector';
import { AutoSplitNotice } from 'containercomponents/PaymentMethods/NewPaymentTypeSelector/components/AutoSplitNotice';
import { PaymentNotice } from 'containercomponents/PaymentMethods/NewPaymentTypeSelector/components/PaymentNotice';
import PaymentMethodContainer from 'containers/PaymentMethodContainer/PaymentMethodContainer';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import {
  MaintenancePaymentPlan,
  MaintenancePaymentMethod,
  PaymentMethod,
  PaymentPreference,
  PaymentPriority,
  PaymentType,
} from 'models/enums';
import { ApprovalContext } from 'pages/PostInspectionFlow/steps/Improvements/Approval/ApprovalContext';
import { QuestionsOrConcernsModalFooter } from 'post-inspection/components/question-or-concern-modal-footer/question-or-concern-modal-footer';
import { STRINGS } from 'post-inspection/constants/strings';
import {
  fetchUserAutoSplitPaymentMethod,
  updateUserAutoSplitPaymentMethod,
} from 'store/redux/user/actions';
import {
  selectFullUser,
  selectUserAutoSplit,
} from 'store/redux/user/selectors';
import { getString } from 'strings';

export const PaymentMethodSplitStep = ({
  onNextStepClick,
}: {
  onNextStepClick: () => void;
}) => {
  const {
    homeownerPayment,
    homeownerReportPayment,
    setIsSubmitting,
    onApprovalStepSubmit,
    onNext,
    isDoNotExceedGroup,
    isLegacyInvoicing,
  } = useContext(ApprovalContext);
  const fullUser = useSelector(selectFullUser);
  const isAutoSplitEnabled = useSelector(selectUserAutoSplit);
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const isInvoicedByGroup = isLegacyInvoicing && isDoNotExceedGroup;

  const { isEnabled: isSplitItEnabled } = useFeatureFlag('enable-split-it');
  const [chosenPaymentType, setSelectedPaymentType] = useState<string>();
  const selectedPaymentType = isSplitItEnabled
    ? chosenPaymentType
    : PaymentType.Payment;
  const [isLoading, setIsLoading] = useState(true);
  // `useState(null)` typed the state as `null`, so the message set below could
  // never be stored.
  const [autoSplitError, setAutoSplitError] = useState<string | null>(null);

  const isMobile = width <= BREAKPOINTS_WIDTHS.SM;

  const { groupDueNow, groupDueNowWithoutSplitIt } =
    homeownerReportPayment || {};

  const userPrimaryPaymentMethod = fullUser?.getPaymentMethodFor(
    PaymentPreference.Maintenance,
    PaymentPriority.Primary,
  );

  const updateUserAutoSplit = async (isEnabled: boolean) => {
    try {
      await dispatch(updateUserAutoSplitPaymentMethod(isEnabled));
      return true;
    } catch (err) {
      console.error(err);
      // `err` is `unknown` in a catch clause; this reads it exactly as before.
      if ((err as any)[0].message.includes('Yearly Payout Plan')) {
        setAutoSplitError(
          'Split It payment method can not be enabled because you are in a Yearly Payout Plan.',
        );
        setTimeout(() => {
          setAutoSplitError(null);
        }, 6000);
      }
      setIsSubmitting(false);
      return false;
    }
  };

  const handleDepositOrNext = async (data: any) => {
    const depositResponse = await onApprovalStepSubmit({ groupPayment: data });
    if (depositResponse) onNext({ refreshFlow: true });
  };

  const getPaymentMethod = () => {
    if (selectedPaymentType === PaymentType.AutoSplit) return null;

    return userPrimaryPaymentMethod.paymentMethod === PaymentMethod.Ach
      ? MaintenancePaymentMethod.Check
      : userPrimaryPaymentMethod.paymentMethod;
  };

  const handlePayNow = async () => {
    try {
      setIsSubmitting(true);

      const payload = {
        ...homeownerPayment,
        deferPaymentUntilLease: null,
        installmentCount: 1,
        paymentPlan: MaintenancePaymentPlan.OneTime,
        paymentMethod: getPaymentMethod(),
      };

      const isAutoSplitUpdate =
        isSplitItEnabled &&
        (selectedPaymentType === 'AutoSplit') !== isAutoSplitEnabled;

      if (isAutoSplitUpdate) {
        const updated = await updateUserAutoSplit(!isAutoSplitEnabled);

        if (!updated) return;
      }

      if (selectedPaymentType === PaymentType.AutoSplit) {
        onNextStepClick();
        return;
      }

      await handleDepositOrNext(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = () => {
    if (typeof selectedPaymentType === 'undefined') return true;
    if (
      selectedPaymentType !== PaymentType.AutoSplit &&
      !userPrimaryPaymentMethod
    ) {
      return true;
    }

    if (selectedPaymentType === PaymentType.AutoSplit) {
      return false;
    }
    return false;
  };

  useEffect(() => {
    const fetchAutoSplit = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchUserAutoSplitPaymentMethod());
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setIsLoading(false);
      }
    };
    fetchAutoSplit();
  }, []);

  return (
    <div className="pt-xl sm:pt-2xl flex w-full flex-col items-center justify-between">
      <div className="flex w-full flex-col items-center">
        <div className="text-center sm:max-w-[340px] md:max-w-[700px]">
          {(!isLegacyInvoicing || !isDoNotExceedGroup) && groupDueNow > 0 && (
            <div className="mb-2sm flex justify-center">
              <IconCheck width={20} className="mr-2xs text-green" />
              <Text fontWeight="semibold" variant="body">
                {getString(STRINGS['deposit-paid'], {
                  deposit: formatMoney(groupDueNow, 'DOLLARS_WITH_CENTS'),
                })}
              </Text>
            </div>
          )}
          <Text fontWeight="semibold" variant="h2">
            {getString(STRINGS['payment-method-step-title-split'])}
          </Text>
        </div>
        {!isInvoicedByGroup && (
          <div className="pt-xs text-center sm:max-w-[560px]">
            <Text>{STRINGS['payment-method-split-step-subtitle']} </Text>
          </div>
        )}
        <div className="my-xl w-full sm:mb-0">
          <div
            className={cx(
              'md:px-sm',
              typeof selectedPaymentType === 'undefined' ? 'pb-2xl' : '',
            )}
          >
            <>
              {isSplitItEnabled && (
                <Selector
                  fluid
                  selected={selectedPaymentType}
                  onSelect={(value: string) => setSelectedPaymentType(value)}
                  buttonClassName="md:flex-col flex-row items-center"
                  iconClassName="self-center"
                  labelClassName="text-center w-auto"
                  mobileTag
                  buttons={[
                    {
                      label: (
                        <>
                          <p className="mt-xs text-body text-left font-semibold md:mt-0 md:text-center">
                            Split It
                          </p>
                          <p className="mb-xs text-p1 text-left font-[400] md:mb-0 md:text-center">
                            Interest Applied ·{' '}
                            {isInvoicedByGroup
                              ? `${formatMoney(groupDueNow, 'DOLLARS_WITH_CENTS')} Deposit`
                              : 'Installments'}
                          </p>
                        </>
                      ),
                      icon: (
                        <Image
                          src="homeowner-earnings/split-it-tile.svg"
                          alt="Split It"
                          className="md:mt-sm md:w-icons-96x96 mt-0 inline-block h-[70px]  w-[70px] object-contain md:h-[96px]"
                        />
                      ),
                      iconSelected: (
                        <Image
                          src="homeowner-earnings/split-it-tile-active.svg"
                          alt="Split It"
                          className="md:mt-sm md:w-icons-96x96 mt-0 inline-block h-[70px]  w-[70px] object-contain md:h-[96px]"
                        />
                      ),
                      key: PaymentType.AutoSplit,
                      tag: 'Recommended',
                    },
                    {
                      label: (
                        <>
                          <p className="text-body font-semibold">
                            Upon Completion
                          </p>
                          <p className="mb-xs text-p1 text-left font-[400] md:mb-0 md:text-center">
                            {isInvoicedByGroup
                              ? `In Full · ${formatMoney(groupDueNowWithoutSplitIt, 'DOLLARS_WITH_CENTS')} Deposit`
                              : 'FREE · In Full'}
                          </p>
                        </>
                      ),
                      icon: (
                        <Image
                          src="homeowner-earnings/payment-tile.svg"
                          alt="Payment Methods"
                          className="md:mt-sm md:w-icons-96x96 mt-0 inline-block h-[70px]  w-[70px] object-contain md:h-[96px]"
                        />
                      ),
                      iconSelected: (
                        <Image
                          src="homeowner-earnings/payment-tile-active.svg"
                          alt="Payment Methods"
                          className="md:mt-sm md:w-icons-96x96 mt-0 inline-block h-[70px]  w-[70px] object-contain md:h-[96px]"
                        />
                      ),
                      key: PaymentType.Payment,
                    },
                  ]}
                />
              )}
              {selectedPaymentType === PaymentType.Payment && (
                <div className="mb-xl">
                  <PaymentNotice />
                  <PaymentMethodContainer
                    maintenanceOnly
                    hasLabels={false}
                    variant="secondary"
                  />
                </div>
              )}
              {selectedPaymentType === PaymentType.AutoSplit && (
                <AutoSplitNotice improvementsReport />
              )}
            </>
            {autoSplitError && (
              <Text className="mb-sm text-red text-[14px]">
                {autoSplitError}
              </Text>
            )}
          </div>
          <QuestionsOrConcernsModalFooter>
            <Button
              disabled={isDisabled() || isLoading}
              onClick={handlePayNow}
              size={isMobile ? 'fluid' : groupDueNow ? 'default' : 'large'}
            >
              {isInvoicedByGroup
                ? selectedPaymentType === PaymentType.AutoSplit
                  ? STRINGS.next
                  : STRINGS.done
                : groupDueNow
                  ? STRINGS.done
                  : STRINGS.next}
            </Button>
          </QuestionsOrConcernsModalFooter>
        </div>
      </div>
    </div>
  );
};
